import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';
import '../../assets/fonts/satoshi.css';

// 1. Modern Healthcare Color Palette
const palette = {
  mode: 'light',
  // Common colors
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  // 60% - Dominant Color (Primary)
  primary: {
    light: '#87CEFA', // Light sky blue
    main: '#4682B4', // Steel blue - dominant color (60%)
    dark: '#1E4D8C', // Dark blue
    text: '#4682B4',
    contrastText: '#FFFFFF',
  },
  // 30% - Secondary Color
  secondary: {
    light: '#B0C4DE', // Light steel blue
    main: '#778899', // Light slate gray - secondary color (30%)
    dark: '#2F4F4F', // Dark slate gray
    contrastText: '#FFFFFF',
  },
  // 10% - Accent Colors
  success: {
    light: '#98FB98', // Pale green
    main: '#3CB371', // Medium sea green
    dark: '#2E8B57', // Sea green
    contrastText: '#FFFFFF',
  },
  warning: {
    light: '#FFE4B5', // Moccasin
    main: '#F4A460', // Sandy brown
    dark: '#D2691E', // Chocolate
    contrastText: '#FFFFFF',
  },
  error: {
    light: '#FFB6C1', // Light pink
    main: '#CD5C5C', // Indian red
    dark: '#8B0000', // Dark red
    contrastText: '#FFFFFF',
  },
  info: {
    light: '#B0E0E6', // Powder blue
    main: '#4682B4', // Steel blue
    dark: '#104E8B', // Dark blue
    contrastText: '#FFFFFF',
  },
  neutral: {
    light: '#F0F8FF', // Alice blue
    main: '#B0C4DE', // Light steel blue
    dark: '#778899', // Light slate gray
    contrastText: '#FFFFFF',
  },
  // semantic tokens for healthcare
  appointment: {
    main: '#3CB371', // Medium sea green
  },
  patient: {
    main: '#4682B4', // Steel blue
  },
  alert: {
    main: '#CD5C5C', // Indian red
  },
  background: {
    default: '#F0F8FF', // Alice blue
    paper: '#FFFFFF',
    subtle: '#F5F5F5', // Light mountain mist
  },
  divider: 'rgba(70, 130, 180, 0.12)',
  text: {
    primary: '#2C3E50', // Dark blue-gray
    secondary: '#34495E', // Darker blue-gray
    disabled: '#95A5A6', // Grayish blue
  },
};

// 2. Modern Typography System
const typography = {
  fontFamily: "'Satoshi', sans-serif",
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { 
    fontSize: '2.5rem', 
    fontWeight: 700, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h2: { 
    fontSize: '2rem', 
    fontWeight: 700, 
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h3: { 
    fontSize: '1.5rem', 
    fontWeight: 600, 
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: { 
    fontSize: '1.25rem', 
    fontWeight: 600, 
    lineHeight: 1.4
  },
  h5: { 
    fontSize: '1.125rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  h6: { 
    fontSize: '1rem', 
    fontWeight: 600, 
    lineHeight: 1.5
  },
  subtitle1: { 
    fontSize: '1rem', 
    fontWeight: 500,
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  subtitle2: { 
    fontSize: '0.875rem', 
    fontWeight: 500,
    lineHeight: 1.57,
    color: palette.text.secondary,
  },
  body1: { 
    fontSize: '1rem', 
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  body2: { 
    fontSize: '0.875rem', 
    fontWeight: 300,
    lineHeight: 1.57,
    letterSpacing: '0.01em',
  },
  button: { 
    fontSize: '0.875rem', 
    fontWeight: 600, 
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
  caption: { 
    fontSize: '0.75rem', 
    lineHeight: 1.5,
    color: palette.text.secondary,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.6,
    textTransform: 'uppercase',
  },
};

// 3. Enhanced shape, spacing, shadows
const shape = { borderRadius: 12 }; // Modern, smooth corners
const spacing = 8; // Base unit
const shadows = [
  'none',
  '0px 1px 2px rgba(30, 77, 140, 0.06)',
  '0px 2px 4px rgba(30, 77, 140, 0.08)',
  '0px 4px 8px rgba(30, 77, 140, 0.12)',
  '0px 8px 16px rgba(30, 77, 140, 0.08)',
  '0px 12px 24px rgba(30, 77, 140, 0.12)',
  '0px 14px 28px rgba(30, 77, 140, 0.15)',
  '0px 16px 32px rgba(30, 77, 140, 0.18)',
  '0px 20px 40px rgba(30, 77, 140, 0.20)',
  '0px 24px 48px rgba(30, 77, 140, 0.25)',
  '0px 28px 56px rgba(30, 77, 140, 0.30)',
  '0px 32px 64px rgba(30, 77, 140, 0.35)',
  '0px 36px 72px rgba(30, 77, 140, 0.40)',
  '0px 40px 80px rgba(30, 77, 140, 0.45)',
  '0px 44px 88px rgba(30, 77, 140, 0.50)',
  '0px 48px 96px rgba(30, 77, 140, 0.55)',
  '0px 52px 104px rgba(30, 77, 140, 0.60)',
  '0px 56px 112px rgba(30, 77, 140, 0.65)',
  '0px 60px 120px rgba(30, 77, 140, 0.70)',
  '0px 64px 128px rgba(30, 77, 140, 0.75)',
  '0px 68px 136px rgba(30, 77, 140, 0.80)',
  '0px 72px 144px rgba(30, 77, 140, 0.85)',
  '0px 76px 152px rgba(30, 77, 140, 0.90)',
  '0px 80px 160px rgba(30, 77, 140, 0.95)',
  '0px 84px 168px rgba(30, 77, 140, 1.0)',
];

// 4. Component Overrides
const components = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      contained: {
        boxShadow: 'none',
        background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} , ${alpha(palette.primary.main, 1)} )`,
        backdropFilter: 'blur(10px)', 
        borderRadius: '12px',
        WebkitBackdropFilter: 'blur(10px)',
        color: palette.common.white,
        border: `1px solid ${alpha(palette.common.white, 0.2)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.2)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          boxShadow: 'none',
          background: `linear-gradient(135deg, ${alpha(palette.primary.main, 1)} 20%, ${alpha(palette.info.main, 1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      outlined: {
        borderWidth: '1.5px',
        borderColor: alpha(palette.primary.main, 0.3),
        background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, ${alpha(palette.common.white, 0.05)} 100%)`,
        backdropFilter: 'blur(5px)',
        WebkitBackdropFilter: 'blur(5px)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          borderWidth: '1.5px',
          borderColor: alpha(palette.primary.main, 0.5),
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.15)} 0%, ${alpha(palette.common.white, 0.1)} 100%)`,
          transform: 'translateY(-1px)',
          transition: 'all 0.2s ease-in-out'
        },
      },
      text: {
        '&:hover': {
          background: alpha(palette.primary.main, 0.04),
        },
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      sizeMedium: {
        padding: '6px 16px',
        fontSize: '0.875rem',
      },
      sizeLarge: {
        padding: '8px 20px',
        fontSize: '0.9375rem',
      },
    },
    defaultProps: {
      disableElevation: true,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        boxShadow: shadows[3],
        background: `linear-gradient(135deg, ${alpha(palette.primary.light, 0.1)} 20%, ${alpha(palette.info.light, 0.1)} 100%)`,
        backdropFilter: 'blur(5px)',
        border: `1px solid ${alpha(palette.common.white, 0.1)}`,
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        WebkitBackdropFilter: 'blur(20px)', // For Safari support
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(palette.common.white, 0.1)} 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none'
        },
        '&:hover': {
          boxShadow: shadows[6],
          transform: 'translateY(-2px)',
          background: `linear-gradient(145deg, ${alpha(palette.primary.light, 0.1)} 20%, ${alpha(palette.info.light, 0.1)} 100%)`,
        },
      }
    }
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '24px',
        position: 'relative',
        zIndex: 1
      },
      title: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      subheader: {
        fontSize: '0.875rem',
        color: alpha(palette.text.secondary, 0.9),
      }
    }
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '24px',
        position: 'relative',
        zIndex: 1,
        '&:last-child': {
          paddingBottom: '24px',
        }
      }
    }
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        justifyContent: 'flex-end',
        position: 'relative',
        zIndex: 1
      }
    }
  }
};

let theme = createTheme({
  name: 'mountain',
  palette,
  typography,
  shape,
  spacing,
  shadows,
  components
});
theme = responsiveFontSizes(theme);

export default theme;